import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { logoConcepts } from "@/entities/genesis";
import { Button } from "@/shared/ui/button";

interface Props {
  sessionId: string;
  onSelect: (conceptId: string) => Promise<void> | void;
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export function LogoStep({ sessionId, onSelect, onNext, onSkip, onBack }: Props): JSX.Element {
  const q = useQuery({
    queryKey: ["genesis", "logo-concepts", sessionId],
    queryFn: () => logoConcepts(sessionId),
    staleTime: Infinity,
  });
  const [picked, setPicked] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-4xl space-y-8 pb-16" data-testid="logo-step">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c7a25a]">
          A mark of your own
        </p>
        <h2 className="mt-2 font-serif text-3xl leading-tight text-[#f5efe6] md:text-5xl" style={{ fontFamily: "'Fraunces', serif" }}>
          No problem — let's <span className="italic text-[#c7a25a]">design one together</span>.
        </h2>
        <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-[#f5efe6]/70">
          Three concepts, matched to your industry and voice. Pick one now
          — you can refine it later. Not sure? Skip this step; we'll come back to it.
        </p>
      </div>

      {q.isPending && <p className="text-[#f5efe6]/50">Composing concepts…</p>}
      {q.data && q.data.concepts.length === 0 && (
        <p className="text-[#f5efe6]/60">
          No concepts available for this industry yet. Skip for now — you can add a logo later.
        </p>
      )}
      {q.data && q.data.concepts.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3" data-testid="logo-concepts">
          {q.data.concepts.map((c) => {
            const active = picked === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={async () => { setPicked(c.id); await onSelect(c.id); }}
                data-testid={`logo-concept-${c.id}`}
                className={`group flex flex-col items-center gap-4 rounded-3xl border p-6 transition ${
                  active
                    ? "border-[#c7a25a] bg-[#c7a25a]/8"
                    : "border-[#f5efe6]/10 bg-[#0f1613]/70 hover:border-[#c7a25a]/40"
                }`}
              >
                <div
                  className="grid h-40 w-40 place-items-center rounded-2xl bg-[#f5efe6]"
                  // eslint-disable-next-line react/no-danger — SVG is server-authored, not user input.
                  dangerouslySetInnerHTML={{ __html: c.svg }}
                />
                <div className="text-center">
                  <p className="text-[14px] font-medium text-[#f5efe6]">{c.label}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#f5efe6]/50">
                    {c.style_tags.join(" · ")}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <button type="button" onClick={onBack} data-testid="logo-back" className="rounded-full border border-[#f5efe6]/10 px-6 py-3 text-[13px] text-[#f5efe6]/70 hover:text-[#f5efe6]">
          ← Edit my answers
        </button>
        <Button
          onClick={onNext}
          disabled={!picked}
          data-testid="logo-continue"
          className="h-13 rounded-full bg-[#c7a25a] px-8 text-[15px] font-medium text-[#0d1310] hover:bg-[#d4b06e] disabled:opacity-40"
        >
          Use this mark →
        </Button>
        <button
          type="button"
          onClick={onSkip}
          data-testid="logo-skip"
          className="rounded-full border border-[#f5efe6]/10 px-6 py-3 text-[13px] text-[#f5efe6]/70 hover:text-[#f5efe6]"
        >
          Skip — I'll add one later
        </button>
      </div>
    </div>
  );
}
